import React, { useState, useEffect, useCallback, useMemo } from 'react'
import type { SectionType, SongSettings } from '../../types/song'
import {
  PATTERN_OPTIONS,
  PATTERN_HELP_TEXT,
  getSectionTypeLabel,
  validateCustomPattern,
  type RhymeSchemePattern,
} from '../../types/rhymeScheme'
import { SECTION_TYPE_OPTIONS } from '../../types/song'

interface RhymeSchemeSelectorProps {
  settings: SongSettings
  onUpdateRhymeScheme: (sectionType: string, pattern: RhymeSchemePattern, customPattern?: string) => void
}

/**
 * Component for configuring rhyme schemes per section type
 *
 * Part of Scope 21: Per-Section-Type Rhyme Scheme Configuration
 * Allows users to set expected rhyme patterns for each section type (verse, chorus, etc.)
 *
 * Uses local state for custom pattern inputs to avoid controlled input race conditions
 * when the parent state update chain is lengthy. Syncs to props on mount/change.
 */
export const RhymeSchemeSelector: React.FC<RhymeSchemeSelectorProps> = ({
  settings,
  onUpdateRhymeScheme,
}) => {
  // Memoize to prevent useEffect from firing on every render
  const rhymeSchemes = useMemo(() => settings.rhyme_schemes || {}, [settings.rhyme_schemes])

  // Local state for custom pattern inputs - prevents race conditions in controlled input flow
  // When state updates travel through a long callback chain, rapid input events can race
  const [localCustomPatterns, setLocalCustomPatterns] = useState<Record<string, string>>({})

  // Sync local state from props when rhyme_schemes changes externally
  useEffect(() => {
    const newPatterns: Record<string, string> = {}
    Object.entries(rhymeSchemes).forEach(([sectionType, scheme]) => {
      if (scheme?.customPattern) {
        newPatterns[sectionType] = scheme.customPattern
      }
    })
    setLocalCustomPatterns(prev => {
      // Only update if there's an actual difference to avoid unnecessary re-renders
      const hasChanges = Object.keys(newPatterns).some(
        key => newPatterns[key] !== prev[key]
      ) || Object.keys(prev).some(
        key => !newPatterns[key] && rhymeSchemes[key]?.pattern !== 'CUSTOM'
      )
      return hasChanges ? newPatterns : prev
    })
  }, [rhymeSchemes])

  const handlePatternChange = useCallback((sectionType: string, pattern: RhymeSchemePattern) => {
    // When switching to CUSTOM, preserve existing custom pattern if any
    const existingCustom = localCustomPatterns[sectionType]
    if (pattern === 'CUSTOM' && existingCustom) {
      onUpdateRhymeScheme(sectionType, pattern, existingCustom)
    } else {
      onUpdateRhymeScheme(sectionType, pattern)
    }
  }, [onUpdateRhymeScheme, localCustomPatterns])

  const handleCustomPatternChange = useCallback((sectionType: string, customPattern: string) => {
    const upperPattern = customPattern.toUpperCase()
    // Update local state immediately for responsive UI
    setLocalCustomPatterns(prev => ({ ...prev, [sectionType]: upperPattern }))
    // Notify parent of change
    onUpdateRhymeScheme(sectionType, 'CUSTOM', upperPattern)
  }, [onUpdateRhymeScheme])

  return (
    <div data-testid="rhyme-scheme-selector">
      <h3 className="text-heading font-medium text-gray-900 mb-3">Rhyme Schemes</h3>
      <p className="text-sm text-gray-600 mb-4">
        Configure expected rhyme patterns for each section type. These patterns influence visual feedback in the editor.
      </p>

      <div className="space-y-4">
        {SECTION_TYPE_OPTIONS.map(({ value: sectionType }) => {
          const scheme = rhymeSchemes[sectionType] || { pattern: 'FREE' }
          const isCustom = scheme.pattern === 'CUSTOM'

          return (
            <div
              key={sectionType}
              className="border border-gray-200 rounded-lg p-4 bg-gray-50"
              data-testid={`rhyme-scheme-section-${sectionType}`}
            >
              {/* Section Type Label */}
              <div className="mb-2">
                <label className="block text-sm font-medium text-gray-900">
                  {getSectionTypeLabel(sectionType as SectionType)}
                </label>
              </div>

              {/* Pattern Selector */}
              <div className="mb-2">
                <select
                  value={scheme.pattern}
                  onChange={(e) => handlePatternChange(sectionType, e.target.value as RhymeSchemePattern)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 bg-white"
                  data-testid={`rhyme-scheme-pattern-${sectionType}`}
                  aria-label={`Rhyme scheme pattern for ${getSectionTypeLabel(sectionType as SectionType)}`}
                >
                  {PATTERN_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Custom Pattern Input */}
              {isCustom && (
                <div className="mb-2">
                  <input
                    type="text"
                    value={localCustomPatterns[sectionType] || ''}
                    onChange={(e) => handleCustomPatternChange(sectionType, e.target.value)}
                    placeholder="e.g., ABABCC"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 bg-white"
                    data-testid={`rhyme-scheme-custom-${sectionType}`}
                    aria-label={`Custom rhyme pattern for ${getSectionTypeLabel(sectionType as SectionType)}`}
                  />
                  {localCustomPatterns[sectionType] && !validateCustomPattern(localCustomPatterns[sectionType]) && (
                    <p
                      className="text-xs text-red-600 mt-1"
                      data-testid={`rhyme-scheme-error-${sectionType}`}
                    >
                      Pattern must be uppercase letters only (e.g., ABABCC)
                    </p>
                  )}
                </div>
              )}

              {/* Help Text */}
              <p
                className="text-xs text-gray-500"
                data-testid={`rhyme-scheme-help-${sectionType}`}
              >
                {isCustom && localCustomPatterns[sectionType]
                  ? `Custom pattern: ${localCustomPatterns[sectionType]}`
                  : PATTERN_HELP_TEXT[scheme.pattern]}
              </p>
            </div>
          )
        })}
      </div>
    </div>
  )
}
